/**
 * User Bootstrap
 *
 * Repairs missing profile and home workspace rows for auth users.
 *
 * Primary responsibilities:
 * - Build safe default profile fields from auth user data
 * - Recreate missing profile and home workspace records
 *
 * Dependencies:
 * - Supabase service client
 */

import type { SupabaseClient, User } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import type { Database, Tables, TablesInsert } from '@/supabase/types'

type RouteClient = SupabaseClient<Database>
type AuthUser = Pick<User, 'email' | 'id' | 'raw_user_meta_data'>
type ProfileRow = Tables<'profiles'>
type WorkspaceRow = Tables<'workspaces'>

function getServiceClient(passed?: RouteClient) {
  return passed ?? createServiceClient()
}

// --------------

function readAuthUserMetaString(authUser: AuthUser, key: 'full_name' | 'name' | 'picture') {
  const value = authUser.raw_user_meta_data?.[key]
  return typeof value === 'string' ? value.trim() : ''
}

// --------------

export function buildBootstrapEmail(authUser: Pick<AuthUser, 'email' | 'id'>) {
  const normalizedEmail = typeof authUser.email === 'string'
    ? authUser.email.trim().toLowerCase()
    : ''

  if (normalizedEmail) {
    return normalizedEmail
  }

  return `placeholder+${authUser.id.replace(/-/g, '').slice(0, 12)}@example.com`
}

// --------------

export function buildBootstrapDisplayName(authUser: AuthUser) {
  const fullName = readAuthUserMetaString(authUser, 'full_name')
  if (fullName) {
    return fullName.slice(0, 100)
  }

  const name = readAuthUserMetaString(authUser, 'name')
  if (name) {
    return name.slice(0, 100)
  }

  const localPart = buildBootstrapEmail(authUser).split('@')[0]?.trim()
  if (localPart) {
    return localPart.slice(0, 100)
  }

  return `User ${authUser.id.replace(/-/g, '').slice(0, 12)}`
}

// --------------

export function buildBootstrapUsername(userId: string) {
  return `u${userId.replace(/-/g, '')}`.slice(0, 25)
}

// --------------

export function formatBootstrapHomeWorkspaceName(displayName: string | null | undefined) {
  const normalizedDisplayName = displayName?.trim() ?? ''
  const firstName = normalizedDisplayName.split(' ')[0]?.trim() ?? ''

  if (!firstName) {
    return 'My Workspace'
  }

  if (firstName.toLowerCase().endsWith('s')) {
    return `${firstName}' Workspace`
  }

  return `${firstName}'s Workspace`
}

// --------------

export function buildBootstrapProfileInsert(
  authUser: AuthUser
): TablesInsert<'profiles'> {
  return {
    user_id: authUser.id,
    bio: '',
    display_name: buildBootstrapDisplayName(authUser),
    email: buildBootstrapEmail(authUser),
    image_path: '',
    image_url: readAuthUserMetaString(authUser, 'picture').slice(0, 1000),
    profile_context: '',
    use_azure_openai: false,
    username: buildBootstrapUsername(authUser.id)
  }
}

// --------------

// Bootstrap only ever creates the personal default workspace:
// `is_home: true` means "personal default" here, and `kind` is left to
// its 'personal' database default (migration 20261019000000). Losi Home
// households (`kind: 'home'`) are created explicitly via
// POST /api/workspaces — bootstrap must NEVER auto-create one.
export function buildBootstrapHomeWorkspaceInsert(
  userId: string,
  displayName: string | null | undefined
): TablesInsert<'workspaces'> {
  return {
    user_id: userId,
    is_home: true,
    name: formatBootstrapHomeWorkspaceName(displayName),
    default_context_length: 4096,
    default_model: 'losi-omega',
    default_prompt: 'You are a friendly, helpful AI assistant.',
    default_temperature: 0.5,
    description: 'My home workspace.',
    embeddings_provider: 'openai',
    include_profile_context: true,
    include_workspace_instructions: true,
    instructions: ''
  }
}

// --------------

async function getExistingProfile(
  supabase: RouteClient,
  userId: string
) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    throw new Error(error.message)
  }

  return (data as ProfileRow | null) ?? null
}

// --------------

async function getExistingHomeWorkspace(
  supabase: RouteClient,
  userId: string
) {
  const { data, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('user_id', userId)
    .eq('is_home', true)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    throw new Error(error.message)
  }

  return (data as WorkspaceRow | null) ?? null
}

// --------------

function profileNeedsEmailRepair(profile: Pick<ProfileRow, 'email'> | null) {
  return !profile?.email || !profile.email.trim()
}

// --------------

async function insertMissingProfile(
  supabase: RouteClient,
  authUser: AuthUser
) {
  const { data, error } = await supabase
    .from('profiles')
    .insert(buildBootstrapProfileInsert(authUser))
    .select('*')
    .single()

  if (!error) {
    return data as ProfileRow
  }

  if (error.code !== '23505') {
    throw new Error(error.message)
  }

  const existingProfile = await getExistingProfile(supabase, authUser.id)
  if (!existingProfile) {
    throw new Error('Profile bootstrap conflict could not be resolved')
  }

  return existingProfile
}

// --------------

async function repairProfileEmail(
  supabase: RouteClient,
  authUser: AuthUser
) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ email: buildBootstrapEmail(authUser) })
    .eq('user_id', authUser.id)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data as ProfileRow
}

// --------------

async function insertMissingHomeWorkspace(
  supabase: RouteClient,
  userId: string,
  displayName: string | null | undefined
) {
  const { data, error } = await supabase
    .from('workspaces')
    .insert(buildBootstrapHomeWorkspaceInsert(userId, displayName))
    .select('*')
    .single()

  if (!error) {
    return data as WorkspaceRow
  }

  if (error.code !== '23505') {
    throw new Error(error.message)
  }

  const existingWorkspace = await getExistingHomeWorkspace(supabase, userId)
  if (!existingWorkspace) {
    throw new Error('Home workspace bootstrap conflict could not be resolved')
  }

  return existingWorkspace
}

// --------------

export async function ensureUserBootstrap(
  userId: string,
  client?: RouteClient
) {
  const supabase = getServiceClient(client)

  let [profile, homeWorkspace] = await Promise.all([
    getExistingProfile(supabase, userId),
    getExistingHomeWorkspace(supabase, userId)
  ])

  if (profile && homeWorkspace && !profileNeedsEmailRepair(profile)) {
    return {
      homeWorkspaceId: homeWorkspace.id,
      profile
    }
  }

  const {
    data: { user: authUser },
    error: authUserError
  } = await supabase.auth.admin.getUserById(userId)

  if (authUserError || !authUser) {
    throw new Error(authUserError?.message || 'Auth user not found')
  }

  if (!profile) {
    profile = await insertMissingProfile(supabase, authUser)
  } else if (profileNeedsEmailRepair(profile)) {
    profile = await repairProfileEmail(supabase, authUser)
  }

  if (!homeWorkspace) {
    homeWorkspace = await insertMissingHomeWorkspace(
      supabase,
      userId,
      profile.display_name || buildBootstrapDisplayName(authUser)
    )
  }

  return {
    homeWorkspaceId: homeWorkspace.id,
    profile
  }
}
